import { minuteWeights, scoreWindow, windowMinutes } from "./bestTimes.js";
import { randomInt, rngFrom, weightedIndex } from "./rng.js";
import { addDays, assertValidTimeZone, formatDayKey, parseDayKey, toWallClock, weekdayOf, zonedTimeToUtc, } from "./timezone.js";
import { MINUTES_PER_DAY, formatClock, validateWindows, windowContains, } from "./windows.js";
export const DEFAULT_SCHEDULE_RULES = {
    /**
     * Every day, Sunday included.
     *
     * This was Mon–Sat, copied from an example in the spec, and it quietly
     * became policy: a brand that never opened the schedule screen inherited
     * it, and across one real workspace the posts fell 0, 9, 8, 8, 6, 6, 9 by
     * weekday — every day except Sunday, with nothing on screen explaining why.
     *
     * Sunday is a trading day for most of the businesses this is built for, and
     * in much of West Africa it is one of the busiest social days of the week.
     * Deciding otherwise on their behalf is not a default, it is a restriction.
     * Anyone who does want weekdays only can say so, and a preset says it for
     * them.
     */
    activeDays: [0, 1, 2, 3, 4, 5, 6],
    timeWindows: [
        { start: "08:00", end: "10:30" },
        { start: "18:00", end: "21:00" },
    ],
    postsPerWeekMin: 3,
    postsPerWeekMax: 5,
    jitterMinutes: 20,
    minGapHours: 6,
    staggerPlatformsMinutes: 15,
    skipDays: [],
    quietHours: { start: "22:00", end: "06:00" },
    useBestTimes: true,
};
/** How far past the requested day the planner will look for an allowed one. */
const MAX_DAY_SEARCH = 14;
export class SchedulingError extends Error {
    constructor(message) {
        super(message);
        this.name = "SchedulingError";
    }
}
/** Spec §6 step 4: a post must never land exactly on the hour or half hour. */
export function isRoundMinute(minuteOfDay) {
    const m = ((minuteOfDay % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return m % 60 === 0 || m % 60 === 30;
}
/**
 * Minutes of the day this rule set permits on `day`, for `platform`.
 *
 * Returns the minutes and their weights together so a caller can make a single
 * weighted choice. Empty means the rules are self-contradictory for that day —
 * typically quiet hours swallowing the only posting window.
 */
function acceptableMinutes(rules, platform, weekday, windowIndex) {
    const window = rules.timeWindows[windowIndex];
    const all = windowMinutes(window);
    const allWeights = minuteWeights(platform, window, weekday, rules.useBestTimes);
    const minutes = [];
    const weights = [];
    for (let i = 0; i < all.length; i++) {
        const minute = all[i];
        if (isRoundMinute(minute))
            continue;
        if (rules.quietHours && windowContains(rules.quietHours, minute))
            continue;
        minutes.push(minute);
        weights.push(allWeights[i]);
    }
    return { minutes, weights };
}
/** Chooses which of the configured windows to use, weighted by quality. */
function chooseWindowIndex(rng, rules, platform, weekday) {
    const scores = rules.timeWindows.map((w) => scoreWindow(platform, w, weekday, rules.useBestTimes));
    return weightedIndex(rng, scores);
}
/** Nearest acceptable minute to `desired`, or null if the set is empty. */
function snapToAcceptable(minutes, desired) {
    if (minutes.length === 0)
        return null;
    let best = minutes[0];
    let bestDistance = Math.abs(best - desired);
    for (const minute of minutes) {
        const distance = Math.abs(minute - desired);
        if (distance < bestDistance) {
            best = minute;
            bestDistance = distance;
        }
    }
    return best;
}
function isDayAllowed(rules, day) {
    if (rules.skipDays.includes(day))
        return false;
    return rules.activeDays.includes(weekdayOf(day));
}
function breaksGap(candidate, taken, minGapHours) {
    if (minGapHours <= 0)
        return false;
    const gapMs = minGapHours * 3600_000;
    return taken.some((t) => Math.abs(t.getTime() - candidate.getTime()) < gapMs);
}
/**
 * Plans one post's publish time.
 *
 * Throws rather than returning a fallback: a post silently scheduled outside
 * the owner's stated hours is worse than one that visibly failed to schedule,
 * because nobody finds out until it has already posted at 3am.
 */
export function planOne(input) {
    const { rules, timezone, targetDay, seed, platform } = input;
    const taken = input.taken ?? [];
    assertValidTimeZone(timezone);
    validateWindows(rules.timeWindows);
    if (rules.activeDays.length === 0) {
        throw new SchedulingError("No posting days are enabled for this brand.");
    }
    parseDayKey(targetDay); // fail loudly on a malformed day
    let sawAllowedDay = false;
    for (let dayOffset = 0; dayOffset <= MAX_DAY_SEARCH; dayOffset++) {
        const day = addDays(targetDay, dayOffset);
        if (!isDayAllowed(rules, day))
            continue;
        sawAllowedDay = true;
        const weekday = weekdayOf(day);
        const { year, month, day: dom } = parseDayKey(day);
        // A handful of attempts per day: each redraws the window and minute, which
        // is what lets a post dodge a gap conflict without abandoning the day.
        for (let attempt = 0; attempt < 8; attempt++) {
            const rng = rngFrom(seed, platform, day, attempt);
            const windowIndex = chooseWindowIndex(rng, rules, platform, weekday);
            const { minutes, weights } = acceptableMinutes(rules, platform, weekday, windowIndex);
            if (minutes.length === 0)
                continue;
            const base = minutes[weightedIndex(rng, weights)];
            const jitter = rules.jitterMinutes > 0 ? randomInt(rng, -rules.jitterMinutes, rules.jitterMinutes) : 0;
            // Snap back onto the acceptable set: jitter must not push a post outside
            // the window the owner chose, into quiet hours, or onto a round minute.
            const minute = snapToAcceptable(minutes, base + jitter);
            if (minute === null)
                continue;
            const at = zonedTimeToUtc(timezone, year, month, dom, Math.floor(minute / 60), minute % 60);
            if (input.notBefore && at.getTime() < input.notBefore.getTime())
                continue;
            if (breaksGap(at, taken, rules.minGapHours))
                continue;
            return { at, day, localTime: formatClock(minute), movedDays: dayOffset };
        }
    }
    if (!sawAllowedDay) {
        throw new SchedulingError(`No posting day available within ${MAX_DAY_SEARCH} days of ${targetDay}. ` +
            `Check the active days and skipped dates.`);
    }
    throw new SchedulingError(`Could not place a post near ${targetDay} without breaking the ` +
        `${rules.minGapHours}-hour minimum gap. Widen the posting windows or reduce the gap.`);
}
/**
 * Plans every platform for one post (spec §6 step 5).
 *
 * The first variant sets the anchor; the rest are offset from it by a random
 * amount inside `staggerPlatformsMinutes`, so the same content doesn't appear
 * on four networks in the same second — which is the most obvious possible
 * tell that an account is automated.
 *
 * A staggered time still has to satisfy that variant's OWN rules. Where it
 * can't — a page with narrower windows, or a stagger that crosses midnight into
 * quiet hours — that variant is planned independently rather than forced.
 */
export function planVariants(input) {
    const { timezone, targetDay, seed, variants } = input;
    if (variants.length === 0)
        return [];
    const anchorSpec = variants[0];
    const anchor = planOne({
        rules: anchorSpec.rules,
        timezone,
        targetDay,
        seed,
        platform: anchorSpec.platform,
        taken: anchorSpec.taken,
        notBefore: input.notBefore,
    });
    const planned = [
        { ...anchor, key: anchorSpec.key, platform: anchorSpec.platform },
    ];
    let cumulativeOffset = 0;
    for (let i = 1; i < variants.length; i++) {
        const spec = variants[i];
        const rng = rngFrom(seed, "stagger", spec.key, i);
        const stagger = spec.rules.staggerPlatformsMinutes;
        let placed = null;
        if (stagger > 0) {
            cumulativeOffset += randomInt(rng, 1, Math.max(1, stagger));
            const candidate = new Date(anchor.at.getTime() + cumulativeOffset * 60_000);
            placed = acceptStaggered(candidate, spec, timezone, input.notBefore);
        }
        planned.push(placed ??
            // Independent fallback: a different seed so it doesn't land back on the
            // anchor's minute and undo the staggering.
            {
                ...planOne({
                    rules: spec.rules,
                    timezone,
                    targetDay,
                    seed: `${seed}:${spec.key}`,
                    platform: spec.platform,
                    taken: spec.taken,
                    notBefore: input.notBefore,
                }),
                key: spec.key,
                platform: spec.platform,
            });
    }
    return planned;
}
/** Whether a staggered instant is legal under one variant's own rules. */
function acceptStaggered(candidate, spec, timezone, notBefore) {
    if (notBefore && candidate.getTime() < notBefore.getTime())
        return null;
    const wall = toWallClock(candidate, timezone);
    const minute = wall.hour * 60 + wall.minute;
    const day = formatDayKey(wall);
    if (!isDayAllowed(spec.rules, day))
        return null;
    if (isRoundMinute(minute))
        return null;
    if (spec.rules.quietHours && windowContains(spec.rules.quietHours, minute))
        return null;
    if (!spec.rules.timeWindows.some((w) => windowContains(w, minute)))
        return null;
    if (breaksGap(candidate, spec.taken ?? [], spec.rules.minGapHours))
        return null;
    return {
        at: candidate,
        day,
        localTime: formatClock(minute),
        movedDays: 0,
        key: spec.key,
        platform: spec.platform,
    };
}
/**
 * Chooses which days in a range should carry a post (spec §6 step 2).
 *
 * Used by weekly generation and "Plan my month" to turn "about 4 posts a week"
 * into actual dates. Spreading rather than front-loading matters: filling
 * Monday–Thursday and leaving the weekend empty reads as a batch job.
 */
export function pickPostingDays(input) {
    const { rules, from, days, seed } = input;
    const rng = rngFrom(seed, "days", from, days);
    const allowed = [];
    for (let i = 0; i < days; i++) {
        const day = addDays(from, i);
        if (isDayAllowed(rules, day))
            allowed.push(day);
    }
    if (allowed.length === 0)
        return [];
    const weeks = days / 7;
    const wanted = input.count ??
        Math.round(randomInt(rng, rules.postsPerWeekMin, rules.postsPerWeekMax) * weeks);
    const target = Math.min(Math.max(wanted, 0), allowed.length);
    if (target === 0)
        return [];
    if (target === allowed.length)
        return allowed;
    // Even spacing with a seeded wobble, rather than a random sample: a uniform
    // sample clusters, and clusters are what make an account look scheduled.
    const stride = allowed.length / target;
    const chosen = new Set();
    for (let i = 0; i < target; i++) {
        const centre = i * stride;
        const wobble = rng() * stride;
        let index = Math.min(allowed.length - 1, Math.floor(centre + wobble));
        while (chosen.has(index) && index < allowed.length - 1)
            index++;
        while (chosen.has(index) && index > 0)
            index--;
        chosen.add(index);
    }
    return [...chosen].sort((a, b) => a - b).map((i) => allowed[i]);
}
//# sourceMappingURL=scheduler.js.map